// The orb renderer: orb/playground/index.html (v48, 2026-09-22) ported
// verbatim in behaviour. The playground and this file are one renderer in
// two shells: a change to one is made to the other in the same commit.
//
// Playground-only and not here: the control panel, the URL hash, toasts,
// simulated speech, the microphone (frames come from the Rust side), the
// Silero VAD (see audio.ts). The constants are the playground's defaults;
// they are exposed as OrbConfig.

import { createAnalysis } from "./audio";
import { MARK_CX, MARK_CY, MARK_D, MARK_FILL_RULE, MARK_W } from "./mark";

// The mode the orb holds (blueprint D9): what the pill under the orb says
// until it changes. The disc always shows the brand mark.
export type OrbMode = "normal" | "dictate" | "record" | "meeting";
export const MODES: OrbMode[] = ["normal", "dictate", "record", "meeting"];
export const MODE_WORD: Record<OrbMode, string> = {
  normal: "",
  dictate: "write",
  record: "rec",
  meeting: "meet",
};

export type OrbState =
  | "idle"
  | "listening"
  | "thinking"
  | "speaking"
  | "acting"
  | "needs";
export const STATES: OrbState[] = [
  "idle",
  "listening",
  "thinking",
  "speaking",
  "acting",
  "needs",
];

// The playground's parameters, at its current defaults.
export type OrbConfig = {
  size: number;
  breath: number;
  gap: number;
  bars: number;
  reach: number;
  mirror: number;
  react: number;
  pump: number;
  spinT: number;
  spinA: number;
  fluid: number;
  trail: number;
  float: number;
};
export const DEFAULTS: OrbConfig = {
  size: 54,
  breath: 4,
  gap: 16,
  bars: 56,
  reach: 26,
  mirror: 1,
  react: 25,
  pump: 40,
  spinT: 1.5,
  spinA: 6,
  fluid: 50,
  trail: 5,
  float: 3,
};

type RGB = [number, number, number];
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const TAU2 = Math.PI * 2;

// ---------- physics ----------
// critically-damped-ish spring step
// Whether the orb is drawn in the recording red. The record mode is red
// throughout: its notes are kept whatever the state. A meeting is red
// only while it listens — while the server records it. At rest, writing
// the meeting up, or dialling again after a lost connection, it is not
// recording, and must not look as if it were (PRODUCT-169).
export function recordsRed(mode: OrbMode, state: OrbState): boolean {
  return mode === "record" || (mode === "meeting" && state === "listening");
}

export function spring(
  pos: number,
  vel: number,
  target: number,
  k: number,
  c: number,
  dt: number,
): [number, number] {
  const a = k * (target - pos) - c * vel;
  vel += a * dt;
  pos += vel * dt;
  return [pos, vel];
}

export type Body = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  target: { x: number; y: number } | null;
};

// orb body: springs toward the pointer while dragged, coasts with friction after release, bounces off edges.
export function stepBody(
  orb: Body,
  dt: number,
  F: number,
  W: number,
  H: number,
  rad: number,
) {
  if (orb.target) {
    const k = lerp(400, 120, F),
      c = lerp(34, 16, F);
    [orb.x, orb.vx] = spring(orb.x, orb.vx, orb.target.x, k, c, dt);
    [orb.y, orb.vy] = spring(orb.y, orb.vy, orb.target.y, k, c, dt);
  } else {
    // coast: exponential drag plus a constant friction so it actually stops instead of creeping
    const fr = Math.pow(lerp(0.02, 0.2, F), dt);
    orb.vx *= fr;
    orb.vy *= fr;
    const spd = Math.hypot(orb.vx, orb.vy),
      dec = lerp(1400, 700, F) * dt; // px/s² of dry friction
    if (spd <= dec + 12) {
      orb.vx = 0;
      orb.vy = 0;
    } else {
      orb.vx -= (orb.vx / spd) * dec;
      orb.vy -= (orb.vy / spd) * dec;
    }
    orb.x += orb.vx * dt;
    orb.y += orb.vy * dt;
    const m = rad * 1.2; // soft walls with bounce
    if (orb.x < m) {
      orb.x = m;
      orb.vx = Math.abs(orb.vx) * 0.45;
    }
    if (orb.x > W - m) {
      orb.x = W - m;
      orb.vx = -Math.abs(orb.vx) * 0.45;
    }
    if (orb.y < m) {
      orb.y = m;
      orb.vy = Math.abs(orb.vy) * 0.45;
    }
    if (orb.y > H - m) {
      orb.y = H - m;
      orb.vy = -Math.abs(orb.vy) * 0.45;
    }
  }
}

// ---------- state weights ----------
// per-state weights cross-fade instead of cutting; tau = lerp(0.12, 0.45, fluid)
export function stepWeights(
  W8: Record<OrbState, number>,
  state: OrbState,
  dt: number,
  F: number,
) {
  const tau = lerp(0.12, 0.45, F);
  for (const s of STATES)
    W8[s] += ((s === state ? 1 : 0) - W8[s]) * Math.min(1, dt / tau);
  return tau;
}

// ---------- palette ----------
const MINT: RGB = [143, 240, 216];
// per-state palette (Daniel, 2026-09-16): speaking pink, acting red, needs-you violet; the rest mint
export const PAL: Record<OrbState, RGB[]> = {
  // six hues, one per state (v11): highlight / body / rim
  idle: [
    [178, 236, 238],
    [94, 213, 220],
    [40, 140, 146],
  ],
  listening: [
    [200, 255, 220],
    [124, 242, 164],
    [52, 170, 98],
  ],
  thinking: [
    [240, 215, 185],
    [196, 149, 106],
    [140, 96, 60],
  ],
  speaking: [
    [255, 200, 226],
    [255, 122, 182],
    [200, 70, 130],
  ],
  acting: [
    [255, 190, 190],
    [255, 90, 90],
    [190, 50, 50],
  ],
  needs: [
    [220, 210, 255],
    [165, 139, 255],
    [106, 77, 224],
  ],
};
// The ring's colours: four real hues per state, not one hue at three
// lightnesses — what the product page's orb does with pink-violet-blue, done
// inside each state's family. Nothing pale in here: PAL's light stop read as
// white in the ring. The family still answers the one question: greens and
// cyans while it manages, purples and pinks when it needs you.
export const RING: Record<OrbState, RGB[]> = {
  idle: [
    [143, 240, 216],
    [70, 200, 235],
    [60, 150, 235],
    [120, 235, 170],
  ],
  listening: [
    [124, 242, 164],
    [80, 230, 220],
    [60, 190, 240],
    [170, 240, 120],
  ],
  thinking: [
    [196, 149, 106],
    [235, 190, 110],
    [200, 120, 90],
    [240, 210, 150],
  ],
  speaking: [
    [255, 122, 182],
    [255, 150, 120],
    [230, 100, 210],
    [255, 180, 140],
  ],
  acting: [
    [255, 90, 90],
    [255, 140, 70],
    [230, 70, 140],
    [255, 120, 100],
  ],
  needs: [
    [165, 139, 255],
    [230, 110, 220],
    [90, 110, 240],
    [200, 150, 255],
  ],
};
// Recording: in the record mode, listening is a recorder's red instead of
// green, so a glance says the note is being taken (2026-09-26, Nuutti:
// "äänitystilassa … orbin väri vois olla joku punasempi"). Only listening
// changes; the bars, the breath and every other state are as they are.
const REC_PAL: RGB[] = [
  [255, 196, 196],
  [240, 72, 82],
  [170, 34, 50],
];
const REC_RING: RGB[] = [
  [240, 72, 82],
  [255, 112, 92],
  [214, 46, 104],
  [255, 142, 122],
];
export const recording = (
  table: Record<OrbState, RGB[]>,
  rec: RGB[],
): Record<OrbState, RGB[]> => ({ ...table, listening: rec });
export const PAL_REC = recording(PAL, REC_PAL);
export const RING_REC = recording(RING, REC_RING);
// the core inside the ring: ink tinted 10 % toward the state's body colour
const INK: RGB = [24, 29, 34];
export const mixRGB = (a: RGB, b: RGB, t: number): RGB => [
  Math.round(lerp(a[0], b[0], t)),
  Math.round(lerp(a[1], b[1], t)),
  Math.round(lerp(a[2], b[2], t)),
];
export function blend(
  W8: Record<OrbState, number>,
  table: Record<OrbState, RGB[]>,
  idx: number,
): RGB {
  // weighted mix over state weights
  let r = 0,
    g = 0,
    b = 0,
    sw = 0;
  for (const st of STATES) {
    const w = W8[st];
    if (w < 0.001) continue;
    const c = table[st][idx];
    r += c[0] * w;
    g += c[1] * w;
    b += c[2] * w;
    sw += w;
  }
  return sw > 0
    ? [Math.round(r / sw), Math.round(g / sw), Math.round(b / sw)]
    : MINT;
}
const col = (rgb: RGB, a: number) => `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${a})`;

// ---------- spin ----------
// top-spin with a real cruise: calm ramp-up, sustained spin, ramp-down, short rest, repeat.
export const spinVel = (
  since: number,
  up: number,
  cruise: number,
  down: number,
  rest: number,
  rps: number,
) => {
  const w = rps * 2 * Math.PI,
    T = up + cruise + down + rest,
    u = since - Math.floor(since / T) * T;
  if (u < up) return (w * (1 - Math.cos((Math.PI * u) / up))) / 2;
  if (u < up + cruise) return w;
  if (u < up + cruise + down)
    return (w * (1 + Math.cos((Math.PI * (u - up - cruise)) / down))) / 2;
  return 0;
};

export type Spin = { omega: number; angle: number; velH: number }; // glyph angular velocity, accumulated angle, settle-spring velocity

// We integrate angular VELOCITY, never blend angles: leaving the state just brakes to a stop.
export function stepSpin(
  s: Spin,
  state: OrbState,
  since: number,
  dt: number,
  P: OrbConfig,
) {
  let omegaT = 0;
  if (state === "thinking")
    omegaT = -spinVel(since, 0.6, 1.4, 0.6, 0.6, P.spinT);
  else if (state === "acting")
    omegaT = spinVel(since, 0.5, 1.5, 0.5, 0.5, P.spinA);
  const spinning = (state === "thinking" || state === "acting") && omegaT !== 0;
  if (spinning) {
    s.omega += (omegaT - s.omega) * Math.min(1, dt / 0.08);
    s.angle += s.omega * dt;
    s.velH = 0;
  } else {
    // rest phase inside the state, or outside it: brake and settle upright on the nearest full turn
    s.omega += (0 - s.omega) * Math.min(1, dt / 0.2);
    s.angle += s.omega * dt;
    const home = Math.round(s.angle / (2 * Math.PI)) * 2 * Math.PI;
    const k = 260,
      c = 2 * Math.sqrt(k);
    [s.angle, s.velH] = spring(s.angle, s.velH, home, k, c, dt);
    if (Math.abs(s.angle - home) < 1e-3 && Math.abs(s.velH) < 1e-3) {
      s.angle = home;
      s.velH = 0;
    }
  }
  return s.angle;
}

// ---------- the hover ring (PRODUCT-85) ----------
// Resting the pointer on the orb for a moment — a dwell, so that passing
// over it does nothing — shows the modes as smaller balls around it
// (Nuutti's design, 2026-09-23). Clicking a ball switches the mode, like
// Alt+N / Alt+D / Alt+R. The window is click-through everywhere except
// where hit_test says, so while the ring is open the renderer reports a
// bigger sphere radius (hitRadius below) and hit_test tells it when the
// cursor left (orb:cursor).
export const HOVER_MS = 400; // the dwell: how long the pointer rests before the ring opens
export const HOVER_SLACK = 6; // a resting hand is never perfectly still: wobble under this does not reset the dwell
export const HOVER_RADIUS = 1.4; // the sphere's interaction radius, in radii (the drag zone's)
export const BALL_R = 15; // a mode ball's radius
export const RING_GAP = 44; // the ring's distance from the centre: the sphere's radius plus this
export const RING_MARGIN = 6; // the reported region must exceed the balls' outer edge by this
// The balls spread on an arc facing the screen's interior: 3 balls ±40°
// around the facing, 4 balls ±60°.
export const RING_SPREAD = 40; // degrees between neighbouring balls

// Compact mode (D27, amended 2026-09-28): instead of the orb where the
// user left it, the brand mark alone, small, docked at the bottom centre
// of the screen — for dictating, where the orb is a state to glance at
// and never a thing to aim at. It is the same object, drawn small: the
// mark is already what sits on the core, and the state's colour is still
// the ring around it, so nothing is lost but the size and the reach.
export const COMPACT_SIZE = 30; // the mark's diameter while compact
export const COMPACT_LIFT = 46; // how far its centre sits above the bottom edge

/// Where the compact mark docks in a window of this size. Pinned to the
/// bottom centre: the middle of the screen is where the eye already is
/// when dictating into something, and the bottom edge is out of the way
/// of the text being written.
export function dock(w: number, h: number): { x: number; y: number } {
  return { x: Math.round(w / 2), y: Math.round(h - COMPACT_LIFT) };
}

/// Where the orb rests once nothing is holding it: the dock while
/// compact, and nowhere in particular otherwise — a loose orb stays
/// where it was left, so it has no target at all. Used by the release of
/// a press and by a cancelled one; clearing the target while compact
/// would set the docked mark adrift.
export function restTarget(
  compact: boolean,
  w: number,
  h: number,
): { x: number; y: number } | null {
  return compact ? dock(w, h) : null;
}

/// What turning compact on or off does. `dropDrag` matters because the
/// press that is holding the orb to the cursor outlives the change: the
/// next move would put the cursor's position back over the dock and the
/// release would clear it, leaving the mark off its dock until the next
/// resize (Greptile on #86). Turning compact off keeps the drag — the
/// orb is loose again and following the cursor is what it should do.
export function compactChange(
  on: boolean,
  w: number,
  h: number,
): { target: { x: number; y: number } | null; dropDrag: boolean } {
  return { target: restTarget(on, w, h), dropDrag: on };
}

/// Whether the hover ring is held shut: by the gate's confirmation box
/// (PRODUCT-85), or by compact — a ring of mode balls around a 30 px
/// mark is a row of targets too small to hit.
export function ringHeldShut(ringBlocked: boolean, compact: boolean): boolean {
  return ringBlocked || compact;
}

export type HoverEvent =
  | { kind: "move"; x: number; y: number; dist: number } // dist: the cursor's distance from the orb's centre
  | { kind: "down" }
  | { kind: "leave" }
  | { kind: "cursor"; inside: boolean }; // the click-through failsafe (orb:cursor)

export type Hover = {
  open: boolean;
  inside: boolean; // the cursor is inside the interaction radius
  anchorX: number; // where the cursor came to rest
  anchorY: number;
  since: number | null; // when it did
};

export function hoverInit(): Hover {
  return { open: false, inside: false, anchorX: 0, anchorY: 0, since: null };
}

// The ring closes and the dwell starts over.
function closeHover(h: Hover): boolean {
  h.open = false;
  h.inside = false;
  h.since = null;
  return false;
}

// The dwell state machine, stepped once per frame with the pointer event
// the frame collected (or none). `radius` is the interaction radius right
// now: the sphere's while the ring is closed, the reported hit region's
// while it is open — the events the webview receives never come from
// outside it (the rest of the window is click-through), so a move past it
// is a real exit and closes an open ring.
export function stepHover(
  h: Hover,
  now: number,
  ev: HoverEvent | null,
  dragging: boolean,
  radius: number,
): boolean {
  if (dragging) return closeHover(h);
  if (ev) {
    if (ev.kind === "down" || ev.kind === "leave") return closeHover(h);
    if (ev.kind === "cursor") return ev.inside ? h.open : closeHover(h);
    // move past the interaction radius: the dwell resets and an open ring
    // closes (in the Tauri shell the events have already stopped by then —
    // this is the playground's distance-based close)
    if (ev.dist > radius) return closeHover(h);
    if (!h.inside || h.since === null) {
      h.inside = true;
      h.anchorX = ev.x;
      h.anchorY = ev.y;
      h.since = now;
    } else if (Math.hypot(ev.x - h.anchorX, ev.y - h.anchorY) > HOVER_SLACK) {
      h.anchorX = ev.x;
      h.anchorY = ev.y;
      h.since = now;
    }
  }
  if (!h.open && h.inside && h.since !== null && now - h.since >= HOVER_MS)
    h.open = true;
  return h.open;
}

export type RingBall = { x: number; y: number; angle: number };
export type RingLayout = {
  facing: number; // the direction toward the screen's interior
  distance: number; // the balls' distance from the orb's centre
  balls: RingBall[];
};

// The balls sit on an arc facing the screen's interior: the orb rests in a
// corner, so the facing is the direction from the orb toward the screen's
// centre. The distance shrinks if a ball would leave the canvas layer
// (a fixed 800 × 260 sheet that follows the orb); at the resting position
// it never does.
export function ringLayout(
  orbX: number,
  orbY: number,
  rad: number,
  count: number,
  W: number,
  H: number,
  ballR: number = BALL_R,
): RingLayout {
  const facing = Math.atan2(H / 2 - orbY, W / 2 - orbX);
  const angles: number[] = [];
  for (let i = 0; i < count; i++)
    angles.push(facing + ((i - (count - 1) / 2) * RING_SPREAD * Math.PI) / 180);
  const limitX = CW / 2 - ballR - 2,
    limitY = CH / 2 - ballR - 2;
  let distance = rad + RING_GAP;
  for (const a of angles) {
    const dx = Math.abs(Math.cos(a)),
      dy = Math.abs(Math.sin(a));
    distance = Math.min(
      distance,
      dx > 1e-6 ? limitX / dx : Infinity,
      dy > 1e-6 ? limitY / dy : Infinity,
    );
  }
  return {
    facing,
    distance,
    balls: angles.map((a) => ({
      x: orbX + Math.cos(a) * distance,
      y: orbY + Math.sin(a) * distance,
      angle: a,
    })),
  };
}

// Which ball the point lands on, or null.
export function ballAt(
  l: RingLayout,
  ballR: number,
  x: number,
  y: number,
): number | null {
  let best: number | null = null,
    bestD = ballR;
  for (let i = 0; i < l.balls.length; i++) {
    const d = Math.hypot(x - l.balls[i].x, y - l.balls[i].y);
    if (d <= bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

export type RingAction =
  | { kind: "ball"; i: number }
  | { kind: "orb" }
  | { kind: "empty" };

// The pointerdown decision table while the ring is open: a ball is a mode
// switch, the orb's body closes the ring and lets the normal tap/drag
// proceed, and anywhere else closes the ring and swallows the click.
export function ringAction(
  l: RingLayout,
  ballR: number,
  downX: number,
  downY: number,
  distToOrb: number,
  rad: number,
): RingAction {
  if (distToOrb < rad * HOVER_RADIUS) return { kind: "orb" };
  const i = ballAt(l, ballR, downX, downY);
  return i === null ? { kind: "empty" } : { kind: "ball", i };
}

// The sphere radius the renderer reports: the orb's own while the ring is
// closed; while it is open, one that ×1.4 (App.tsx's factor) covers every
// ball's outer edge plus the margin — or the balls cannot be clicked.
export function hitRadius(
  rad: number,
  open: boolean,
  ringDistance: number,
  ballR: number,
): number {
  if (!open) return rad;
  return (ringDistance + ballR + RING_MARGIN) / HOVER_RADIUS;
}

// ---------- the renderer ----------
// The window is the whole screen, but the canvas is only this big and
// follows the orb: a small layer for the compositor to move, and one it
// paints crisp (a screen-sized canvas layer came out soft on WebKitGTK's
// GPU path). Everything is still drawn in screen coordinates.
const CW = 800,
  CH = 260,
  halfW = CW / 2,
  halfH = CH / 2;
const MIC_RATE = 16000; // the rate of microphone frames that come without one
// The frame loop runs on a timer, not requestAnimationFrame: WebKitGTK
// ticks rAF at 30 fps in this window (measured 2026-09-18: 63 frames per
// 2 s, every one 33 ms), which shows as steps in a fast throw. The timer
// gives ~50 fps. The playground keeps rAF; its browsers run it at 60.
const FRAME_MS = 16;

// What a frame drew, in screen coordinates. The next frame clears only
// this box, not the whole canvas: WebKit uploads a canvas's dirty rect,
// and on the software path every uploaded pixel goes through X. Clearing
// the whole 800 × 260 canvas every frame cost ~75 % of a core across orb,
// WebKit, Xorg and picom (measured 2026-09-26); a lower frame rate was no
// way out, as the float and the breath step at 20 fps.
export type Box = { x0: number; y0: number; x1: number; y1: number };
export const grow = (
  b: Box | null,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): Box =>
  b
    ? {
        x0: Math.min(b.x0, x0),
        y0: Math.min(b.y0, y0),
        x1: Math.max(b.x1, x1),
        y1: Math.max(b.y1, y1),
      }
    : { x0, y0, x1, y1 };
/// The rect to clear for a box: whole pixels, 2 px out for antialiasing.
export function clearArea(b: Box): [number, number, number, number] {
  const x = Math.floor(b.x0) - 2,
    y = Math.floor(b.y0) - 2;
  return [x, y, Math.ceil(b.x1) + 2 - x, Math.ceil(b.y1) + 2 - y];
}

export function createOrb(
  canvas: HTMLCanvasElement,
  config: Partial<OrbConfig> = {},
) {
  const ctx = canvas.getContext("2d")!;
  const P: OrbConfig = { ...DEFAULTS, ...config };
  let state: OrbState = "idle";
  let dpr = 1,
    W = 0,
    H = 0;
  let ox = 0,
    oy = 0; // the canvas's offset on the screen
  let level = 0,
    pumpLv = 0;
  const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;

  // audio: the microphone frames from Rust while listening; the reply's playback while speaking
  let mic = createAnalysis(MIC_RATE, P),
    micRate = MIC_RATE;
  let micAt = 0; // when the last frame came; listening bars read the mic only while frames flow
  let output: AnalyserNode | null = null,
    out: ReturnType<typeof createAnalysis> | null = null,
    obuf: Float32Array<ArrayBuffer> | null = null;
  let spectrum: Float32Array = new Float32Array(P.bars);

  const orb: Body = { x: 0, y: 0, vx: 0, vy: 0, target: null };
  let placed = false;
  // wave anchor: a second, softer body that trails the orb, so waves lag and settle after a move.
  const wave = { x: 0, y: 0, vx: 0, vy: 0 };
  const W8 = Object.fromEntries(STATES.map((s) => [s, 0])) as Record<
    OrbState,
    number
  >;
  const sinceT = Object.fromEntries(STATES.map((s) => [s, 0])) as Record<
    OrbState,
    number
  >;
  let rad = 48,
    radV = 0,
    hue = 0;
  const rings: { t0: number }[] = [];
  let lastRing = 0,
    prevLv = 0; // speaking: rings emitted on each syllable onset
  const spin: Spin = { omega: 0, angle: 0, velH: 0 };
  const MARK = new Path2D(MARK_D);
  // The mode: a word in the pill under the orb, cross-faded into the one it
  // replaced so it eases like everything else here.
  let mode: OrbMode = "normal",
    prevMode: OrbMode = "normal",
    pillW = 1,
    floatW = 1;
  // The task pill: `task` beside the mode's word while a task lane runs
  // (PRODUCT-68). Nothing sets it until the lane does (PRODUCT-65); the
  // orb reads the wire already, so the server may ship first.
  let task = false,
    taskW = 0;

  // the one line: text from the server, faded in and out like the playground's per-state lines
  let lineText = "",
    lineW = 0;
  let onTap: ((state: OrbState) => void) | null = null;
  let onContextMenu: (() => void) | null = null;
  let onMove: ((x: number, y: number, r: number, hit: number) => void) | null =
      null,
    movedKey = "";
  // The hover ring (PRODUCT-85): the dwell machine is stepped once per
  // frame with the pointer event the listeners collected. `dev` shows the
  // feedback ball (PRODUCT-87's seam); `ringBlocked` holds the ring shut
  // while the gate's confirmation box is up.
  const hover = hoverInit();
  let hoverEv: HoverEvent | null = null;
  let ringOpen = false, // what the machine said this frame
    ballW = 0, // the balls ease in and out like the pills
    ring: RingLayout | null = null, // this frame's layout, for the drawing and the clicks
    dev = false,
    ringBlocked = false,
    compact = false;
  // A press on a ball, awaiting the release: fired as a mode switch only
  // if the pointer did not move (a tap, like the orb's own).
  let ballDown: { i: number; x: number; y: number } | null = null;
  let onBall: ((mode: OrbMode) => void) | null = null;
  let onFeedback: (() => void) | null = null;

  // ---------- layout ----------
  function resize() {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    // The web page's own: the canvas is the stage in the layout, not a
    // window over the screen — the sphere sits centred in it.
    W = CW;
    H = CH;
    canvas.width = CW * dpr;
    canvas.height = CH * dpr;
    // The canvas scales to its column, both axes together — a circle
    // stays a circle (the pointer mapping reads the rect, so it
    // follows any size).
    const fit = Math.min(1, (canvas.parentElement?.clientWidth || CW) / CW);
    canvas.style.width = `${CW * fit}px`;
    canvas.style.height = `${CH * fit}px`;
    if (!placed) {
      orb.x = halfW;
      orb.y = halfH;
      wave.x = orb.x;
      wave.y = orb.y;
      placed = true;
      rad = P.size / 2;
    }
    // The dock is a fraction of the screen, so a new screen moves it.
    if (compact) orb.target = dock(W, H);
  }
  addEventListener("resize", resize);
  resize();

  function setState(s: OrbState) {
    if (state !== s) sinceT[s] = performance.now();
    state = s;
  }
  sinceT[state] = performance.now();
  setState(state);

  // ---------- drag with inertia ----------
  let drag: { dx: number; dy: number; moved: number } | null = null,
    lastPtr: { x: number; y: number } | null = null;
  // Which ball a click means while the ring is open (PRODUCT-85): the
  // decision table. A ball awaits the release (a tap fires it); the orb's
  // body closes the ring and falls through to the normal drag/tap;
  // anywhere else closes the ring and swallows the click.
  const fireBall = (i: number) => {
    if (dev && i === MODES.length) onFeedback?.();
    else {
      const mode = MODES[i];
      if (mode) onBall?.(mode);
    }
  };
  // The web page's canvas sits in the layout, not over the whole
  // window: the pointer's page coordinates become the canvas's own by
  // the canvas's rect. (The desktop shell moved the window instead.)
  const at = (e: { clientX: number; clientY: number }) => {
    const r = canvas.getBoundingClientRect();
    // The canvas scales to its column (resize's fit below): the
    // mapping follows the scale, not just the offset.
    const k = CW / r.width;
    return { clientX: (e.clientX - r.left) * k, clientY: (e.clientY - r.top) * k };
  };
  canvas.addEventListener("pointerdown", (ev) => {
    const e = { pointerId: ev.pointerId, button: ev.button, ...at(ev) };
    // Only the primary button drags and taps: the middle button does
    // nothing and the right button is the UI's entry point (PRODUCT-86).
    if (e.button !== 0) return;
    const d = Math.hypot(e.clientX - orb.x, e.clientY - orb.y);
    const a =
      ringOpen && ring
        ? ringAction(ring, BALL_R, e.clientX, e.clientY, d, rad)
        : null;
    if (a?.kind === "ball") {
      // The ring stays open until the release: closing it now would
      // shrink the reported region under the held button, and the
      // window would turn click-through before the release arrives.
      ballDown = { i: a.i, x: e.clientX, y: e.clientY };
      canvas.setPointerCapture(e.pointerId);
      return;
    }
    hoverEv = { kind: "down" };
    if (a?.kind === "empty") return;
    // the orb's body: the ring closes (the frame's step) and the
    // normal tap proceeds untouched (PRODUCT-90 owns what a tap does).
    // Compact is taken here too — the press is what a tap is made of
    // (pointerup fires onTap only for a press it took), and a docked
    // mark still starts and stops listening. What compact refuses is
    // the moving, below.
    if (d < rad * 1.4) {
      drag = { dx: orb.x - e.clientX, dy: orb.y - e.clientY, moved: 0 };
      lastPtr = { x: e.clientX, y: e.clientY };
      canvas.setPointerCapture(e.pointerId);
    }
  });
  canvas.addEventListener("pointermove", (ev) => {
    const e = at(ev);
    // The cursor is tracked even when not dragging: the hover dwell reads
    // it. Over the sphere (and over the balls while the ring is open) the
    // window is not click-through, so these are the events there are.
    // A press waiting for this frame's step is not overwritten by a move
    // in the same frame: the press is what closes the ring.
    if (hoverEv?.kind !== "down")
      hoverEv = {
        kind: "move",
        x: e.clientX,
        y: e.clientY,
        dist: Math.hypot(e.clientX - orb.x, e.clientY - orb.y),
      };
    if (!drag || !lastPtr) return;
    // A compact mark is docked: it does not follow the cursor, and
    // dragging it would leave it where the next resize would pull it
    // back from anyway. The distance is still counted, so a press that
    // wandered is not mistaken for a tap.
    if (!compact) orb.target = { x: e.clientX + drag.dx, y: e.clientY + drag.dy };
    drag.moved += Math.hypot(e.clientX - lastPtr.x, e.clientY - lastPtr.y);
    lastPtr = { x: e.clientX, y: e.clientY };
  });
  // A cancelled press (the pointer lost to the system) leaves no ball
  // armed and no drag holding the orb to the cursor.
  canvas.addEventListener("pointercancel", () => {
    ballDown = null;
    drag = null;
    orb.target = restTarget(compact, W, H);
  });
  canvas.addEventListener("pointerleave", () => {
    hoverEv = { kind: "leave" };
  });
  canvas.addEventListener("pointerup", (ev) => {
    const e = at(ev);
    if (ballDown) {
      const b = ballDown;
      ballDown = null;
      if (Math.hypot(e.clientX - b.x, e.clientY - b.y) < 4) fireBall(b.i);
      hoverEv = { kind: "down" };
      return;
    }
    if (drag) {
      const tap = drag.moved < 4;
      drag = null;
      // Letting go of a docked mark leaves it docked: the target is what
      // holds it there, so clearing it would set it adrift.
      orb.target = restTarget(compact, W, H);
      if (tap) onTap?.(state);
    }
  });
  // The right click is the UI's entry point (PRODUCT-86): it never shows
  // the native menu, and on the orb's body it opens the hub window
  // through the callback — the fallback surface D11 allows, not a
  // settings maze (manifesto: no settings). The region is the drag's
  // (rad * 1.4): one press region for both hands of the mouse. (On the
  // desktop the listener is the window's, not the canvas's: the
  // confirmation box beside the orb takes clicks too, and the native
  // menu there offers Reload, which would reload the orb mid-gate. The
  // web page's is the canvas's own — see above.)
  // The web page's canvas: the listener is the canvas's own, so the
  // right-click works everywhere else on the page and dies with the
  // canvas. (The desktop's was the window's — its webview was the
  // orb's whole screen.)
  canvas.addEventListener("contextmenu", (ev: MouseEvent) => {
    ev.preventDefault();
    if (ev.target !== canvas) return;
    const e = at(ev);
    const d = Math.hypot(e.clientX - orb.x, e.clientY - orb.y);
    if (d < rad * 1.4) onContextMenu?.();
  });

  // ---------- audio envelope ----------
  function sampleLevel(dt: number, t: number) {
    let target = 0;
    if (state === "speaking" && out && output && obuf) {
      // speaking is driven by the TTS output, never by the microphone
      output.getFloatTimeDomainData(obuf);
      out.push(obuf);
      const r = out.sample(dt, true);
      target = r.level;
      spectrum = r.spectrum;
      mic.sample(dt, false);
    } else {
      const live = state === "listening" && t - micAt < 250;
      const r = mic.sample(dt, live);
      target = r.level;
      spectrum = r.spectrum;
      out?.sample(dt, false);
    }
    // envelope follower: attack is immediate, release is a short exponential (fall speed: ~60 ms … 250 ms)
    const rel = lerp(0.25, 0.06, P.react / 100);
    if (target > level)
      level += (target - level) * Math.min(1, dt / 0.02); // 20 ms attack: kills FFT frame jitter, still instant to the eye
    else level += (target - level) * Math.min(1, dt / rel);
    // pump envelope: softer (40 ms attack, 250 ms release) and sqrt-boosted so normal speech shows
    const pt = Math.sqrt(Math.max(0, target));
    if (pt > pumpLv) pumpLv += (pt - pumpLv) * Math.min(1, dt / 0.04);
    else pumpLv += (pt - pumpLv) * Math.min(1, dt / 0.25);
    return level;
  }

  // ---------- draw ----------
  let last = performance.now(),
    raf = 0;
  let drawn: Box | null = null,
    atX = NaN,
    atY = NaN; // the canvas offset the drawn box was drawn at
  function frame(t: number) {
    // dt is clamped at zero as well: a first timestamp before the one taken
    // at load made the coast's dry friction divide 0 by 0 in the playground,
    // and an orb whose position is NaN draws nothing and says nothing.
    const dt = Math.min(0.033, Math.max(0, (t - last) / 1000));
    last = t;
    const F = P.fluid / 100; // 0 = stiff, 1 = very loose
    const lv = sampleLevel(dt, t);

    // --- orb body physics ---
    stepBody(orb, dt, F, W, H, rad);
    // wave anchor trails the orb (softer spring)
    if (P.trail > 0) {
      // optional lag for outer effects; 0 = glued to the orb
      const k = lerp(600, 60, P.trail / 100),
        c = 1.6 * Math.sqrt(k);
      [wave.x, wave.vx] = spring(wave.x, wave.vx, orb.x, k, c, dt);
      [wave.y, wave.vy] = spring(wave.y, wave.vy, orb.y, k, c, dt);
    } else {
      wave.x = orb.x;
      wave.y = orb.y;
      wave.vx = 0;
      wave.vy = 0;
    }

    // --- state cross-fade ---
    const tau = stepWeights(W8, state, dt, F);
    const wN = W8.needs;
    hue += (wN - hue) * Math.min(1, dt / (tau * 1.5)); // kept for the 'needs' pulse only
    lineW += ((lineText ? 1 : 0) - lineW) * Math.min(1, dt / tau);
    pillW += (1 - pillW) * Math.min(1, dt / tau);
    taskW += ((task ? 1 : 0) - taskW) * Math.min(1, dt / tau);

    // --- radius springs ---
    const breathe = reduce
      ? 0
      : Math.sin((t / 1000) * ((2 * Math.PI) / P.breath));
    const R = (compact ? COMPACT_SIZE : P.size) / 2;
    const pulse = reduce
      ? 0.5
      : Math.sin(((t - sinceT.needs) / 1000) * 2 * Math.PI * 0.9) * 0.5 + 0.5;
    const targetR = R * (1 + 0.035 * breathe + 0.08 * pulse * W8.needs);
    {
      const k = lerp(500, 180, F);
      [rad, radV] = spring(rad, radV, targetR, k, 2.1 * Math.sqrt(k), dt);
    } // slightly over-damped: never rings
    // audio is applied directly on top of the springs — no filter between the microphone and the pixels
    const radA = rad * (1 + (P.pump / 100) * pumpLv * W8.speaking); // speaking: the orb itself pumps with the voice (soft envelope)

    // --- colours by hue mix ---
    const rec = recordsRed(mode, state);
    const pal = rec ? PAL_REC : PAL;
    const C = blend(W8, pal, 0),
      C2 = blend(W8, pal, 1),
      C3 = blend(W8, pal, 2);

    // the canvas follows the orb; screen coordinates inside it
    ox = Math.round(orb.x - halfW);
    oy = Math.round(orb.y - halfH);
    ctx.setTransform(dpr, 0, 0, dpr, -ox * dpr, -oy * dpr);
    if (ox !== atX || oy !== atY) {
      // the canvas moved: its old pixels sit elsewhere on the screen now
      canvas.style.transform = `translate(${ox}px, ${oy}px)`;
      ctx.clearRect(ox, oy, CW, CH);
      atX = ox;
      atY = oy;
    } else if (drawn) ctx.clearRect(...clearArea(drawn));
    drawn = null;
    const mark = (x0: number, y0: number, x1: number, y1: number) =>
      (drawn = grow(drawn, x0, y0, x1, y1));
    // --- idle float: the orb drifts a few px on a slow figure of eight,
    // drawn as an offset so the body, its drag and its walls know nothing
    // of it; it fades out as the orb is moved or used
    const still = orb.target || Math.hypot(orb.vx, orb.vy) > 5 ? 0 : 1;
    floatW += (still - floatW) * Math.min(1, dt / 0.6);
    const fl = reduce ? 0 : P.float * floatW;
    const fx = Math.sin(((t / 1000) * 2 * Math.PI) / 7) * fl * 0.5,
      fy = Math.sin(((t / 1000) * 2 * Math.PI) / 3.5) * fl * 0.5;
    ctx.save();
    ctx.translate(fx, fy);
    const markF = (x: number, y: number, r: number) =>
      mark(x + fx - r, y + fy - r, x + fx + r, y + fy + r);

    // --- waves: every state drawn at its weight, so transitions blend ---
    const ringW = W8.listening;
    if (ringW > 0.01) {
      // listening: spectrum bars around the orb. Each bar's length is smoothed with its neighbours,
      // so the ring moves as one surface instead of alternating long/short spikes.
      const n = P.bars,
        r0 = radA + P.gap * 0.8;
      const bw = Math.max(1.5, ((TAU2 * r0) / n) * 0.55);
      const val = (i: number) => {
        let bi = ((i % n) + n) % n;
        if (P.mirror) {
          const h = n / 2;
          bi = bi < h ? bi : n - 1 - bi;
        }
        return spectrum[bi];
      };
      ctx.lineCap = "round";
      ctx.lineWidth = bw;
      markF(wave.x, wave.y, r0 + 2 + P.reach + bw);
      for (let i = 0; i < n; i++) {
        const v = (val(i - 1) + 2 * val(i) + val(i + 1)) / 4; // light smoothing: kills alternating spikes, keeps the undulation
        const ang = Math.PI / 2 + (i / n) * TAU2; // start at the bottom
        const len = (2 + P.reach * Math.pow(v, 0.9)) * ringW;
        const x0 = wave.x + Math.cos(ang) * r0,
          y0 = wave.y + Math.sin(ang) * r0;
        const x1 = wave.x + Math.cos(ang) * (r0 + len),
          y1 = wave.y + Math.sin(ang) * (r0 + len);
        const c = v > 0.66 ? C : v > 0.33 ? C2 : C3;
        ctx.strokeStyle = col(c, (0.35 + 0.65 * v) * ringW);
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
        ctx.stroke();
      }
    }
    if (W8.needs > 0.01) {
      const w = W8.needs,
        since = (t - sinceT.needs) / 1000;
      const ph = reduce ? 0.3 : (since * 0.9) % 1;
      ctx.beginPath();
      ctx.arc(wave.x, wave.y, rad + P.gap + ph * P.size * 0.9, 0, Math.PI * 2);
      markF(wave.x, wave.y, rad + P.gap + ph * P.size * 0.9 + 4);
      ctx.strokeStyle = col(C, 0.6 * (1 - ph) * w);
      ctx.lineWidth = (2 + 2 * (1 - ph)) * w;
      ctx.stroke();
    }

    // --- speaking rings: a ring leaves the orb on each rise of the voice (syllable onset) ---
    if (
      W8.speaking > 0.3 &&
      lv > 0.3 &&
      lv - prevLv > 0.04 &&
      t - lastRing > 110
    ) {
      rings.push({ t0: t });
      lastRing = t;
      if (rings.length > 8) rings.shift();
    }
    prevLv = lv;
    for (let i = rings.length - 1; i >= 0; i--) {
      const age = (t - rings[i].t0) / 1000,
        life = 0.9;
      if (age > life) {
        rings.splice(i, 1);
        continue;
      }
      const k = age / life;
      ctx.beginPath();
      ctx.arc(wave.x, wave.y, radA + 6 + k * radA * 1.6, 0, Math.PI * 2);
      markF(wave.x, wave.y, radA + 6 + k * radA * 1.6 + 3);
      ctx.strokeStyle = col(C2, (1 - k) * 0.7 * W8.speaking);
      ctx.lineWidth = 2.5 * (1 - k) + 0.5;
      ctx.stroke();
    }
    // --- the orb: the ring, its core, the mark (v48) ---
    const sp = Math.hypot(orb.vx, orb.vy),
      sq = 1 + Math.min(0.28, sp / 2600) * (0.5 + 0.5 * F);
    {
      const th = Math.atan2(orb.vy, orb.vx); // squash along the motion axis without rotating the content
      ctx.save();
      ctx.translate(orb.x, orb.y);
      ctx.rotate(th);
      ctx.scale(sq, 1 / sq);
      ctx.rotate(-th);
      ctx.translate(-orb.x, -orb.y);
    }
    const rot = stepSpin(spin, state, (t - sinceT[state]) / 1000, dt, P);
    // The ring: the state's four hues flow around it, a conic gradient
    // turning once in ~9 s and with the mark's spin — colour that moves,
    // not light that shades. It thickens on the in-breath.
    const br = 0.5 + 0.5 * breathe;
    const a0 =
      -Math.PI / 2 + rot + (reduce ? 0 : (t / 1000) * ((2 * Math.PI) / 9));
    const cg = ctx.createConicGradient(a0, orb.x, orb.y);
    const R4 = [0, 1, 2, 3].map((i) => blend(W8, rec ? RING_REC : RING, i));
    for (let i = 0; i <= 4; i++) cg.addColorStop(i / 4, col(R4[i % 4], 1));
    ctx.beginPath();
    ctx.arc(orb.x, orb.y, radA, 0, Math.PI * 2);
    markF(orb.x, orb.y, radA * sq);
    ctx.fillStyle = cg;
    ctx.fill();
    const inner = radA * (0.68 - 0.04 * br);
    ctx.beginPath();
    ctx.arc(orb.x, orb.y, inner, 0, Math.PI * 2);
    ctx.fillStyle = col(mixRGB(INK, C2, 0.1), 1);
    ctx.fill();
    // The mark on the core, in the state's body colour, filled evenodd so
    // the bowl's counter stays open; it spins as a whole and swells a
    // little with the breath (speaking has its own pump).
    {
      ctx.save();
      ctx.translate(orb.x, orb.y);
      ctx.rotate(rot);
      const sc =
        ((2 * radA * 0.56) / MARK_W) * (1 + 0.05 * br * (1 - W8.speaking));
      ctx.scale(sc, sc);
      ctx.translate(-MARK_CX, -MARK_CY);
      ctx.fillStyle = col(C2, 1);
      ctx.fill(MARK, MARK_FILL_RULE);
      ctx.restore();
    }
    ctx.restore(); // the squash
    ctx.restore(); // the float

    // --- the pills under the orb: the mode's word, `task` beside it while
    // a task lane runs — one centred group, so a held mode and a running
    // task read as the orb's two held facts. Drawn outside the float and
    // off the breath, on whole pixels: text that drifts by fractions of a
    // pixel shimmers.
    const PILL_FONT =
      '500 11px ui-monospace, "JetBrains Mono", "SF Mono", Menlo, monospace';
    const pillW2 = (word: string) => {
      ctx.font = PILL_FONT;
      ctx.letterSpacing = "0.5px";
      return Math.round(ctx.measureText(word).width + 0.5 + 16);
    };
    const pill = (word: string, cx: number, alpha: number, tint: RGB) => {
      if (!word || alpha < 0.02) return;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.font = PILL_FONT;
      ctx.letterSpacing = "0.5px";
      const tw = pillW2(word),
        th = 18;
      const bx = Math.round(cx) - Math.round(tw / 2),
        by = Math.round(orb.y + R + 8);
      ctx.fillStyle = "rgba(18,22,25,0.92)";
      ctx.strokeStyle = col(tint, 0.5);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(bx + 0.5, by + 0.5, tw, th, th / 2);
      mark(bx, by, bx + tw + 1, by + th + 1);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = col(tint, 1);
      ctx.textBaseline = "middle";
      ctx.textAlign = "center";
      ctx.fillText(word, Math.round(cx) + 0.75, by + th / 2 + 1);
      ctx.restore();
    };
    {
      // A task at work is the orb managing on its own: the managing mint,
      // whatever the turn's state. The mode's slot is the wider of the two
      // words while they cross-fade, so the group does not shift under them.
      const fading = pillW < 0.98 && prevMode !== mode;
      const mSlot = Math.max(
        pillW2(MODE_WORD[mode] || " "),
        fading ? pillW2(MODE_WORD[prevMode] || " ") : 0,
      );
      const hasMode = !!MODE_WORD[mode] || (fading && !!MODE_WORD[prevMode]);
      const tSlot = taskW > 0.02 ? pillW2("task") : 0;
      const gap = 6;
      let x =
        Math.round(orb.x) -
        ((hasMode ? mSlot : 0) + (tSlot && hasMode ? gap : 0) + tSlot) / 2;
      if (hasMode) {
        const cx = x + mSlot / 2;
        if (fading) pill(MODE_WORD[prevMode], cx, 1 - pillW, C2);
        pill(MODE_WORD[mode], cx, pillW, C2);
        x += mSlot + gap;
      }
      if (tSlot) pill("task", x + tSlot / 2, taskW, MINT);
    }

    // --- the hover ring (PRODUCT-85) ---
    // The machine steps once per frame on the collected pointer event. The
    // interaction radius it sees is the region the window actually takes
    // the pointer in: the sphere's while the ring is closed, the reported
    // hit region's while it is open. The gate's confirmation box holds the
    // ring shut (it is answered by voice, the keys or the box).
    {
      const radius = ringOpen
        ? hitRadius(rad, true, rad + RING_GAP, BALL_R) * HOVER_RADIUS
        : rad * HOVER_RADIUS;
      let open = stepHover(hover, t, hoverEv, !!drag, radius);
      hoverEv = null;
      if (ringHeldShut(ringBlocked, compact))
        open = stepHover(hover, t, { kind: "leave" }, false, radius);
      ringOpen = open;
    }
    ballW += ((ringOpen ? 1 : 0) - ballW) * Math.min(1, dt / tau);
    ring =
      ballW > 0.02 ? ringLayout(orb.x, orb.y, rad, dev ? 4 : 3, W, H) : null;
    if (ring) {
      // The balls ease in and out like the pills: dark, with the state's
      // border and label. The conversation ball carries the brand mark,
      // scaled like the disc's; the fourth ball is the dev feedback ball.
      const w = ballW,
        r = BALL_R * w;
      // The balls' text settings stay with the balls. Left on the context,
      // "center" moved the line's text half out of its box, past the area
      // the next frame clears, and the words piled up as ghosts once the
      // ring had been open (2026-09-26; only since 0.4.5 clears no more
      // than was drawn).
      ctx.save();
      ctx.font = PILL_FONT;
      ctx.letterSpacing = "0.5px";
      ctx.textBaseline = "middle";
      ctx.textAlign = "center";
      for (let i = 0; i < ring.balls.length; i++) {
        const b = ring.balls[i];
        ctx.globalAlpha = w;
        ctx.beginPath();
        ctx.arc(b.x, b.y, r, 0, TAU2);
        ctx.fillStyle = "rgba(18,22,25,0.92)";
        ctx.fill();
        ctx.strokeStyle = col(C2, 0.5);
        ctx.lineWidth = 1;
        ctx.stroke();
        const word = dev && i === MODES.length ? "fb" : MODE_WORD[MODES[i]];
        // the label keeps its size while the ball shrinks, and "write" is wider than the ball
        const hw = Math.max(r, word ? ctx.measureText(word).width / 2 + 1 : 0);
        mark(b.x - hw, b.y - Math.max(r, 8), b.x + hw, b.y + Math.max(r, 8));
        if (word) {
          ctx.fillStyle = col(C2, 1);
          ctx.fillText(word, b.x + 0.75, b.y + 1);
        } else {
          ctx.save();
          ctx.translate(b.x, b.y);
          const sc = (2 * r * 0.56) / MARK_W;
          ctx.scale(sc, sc);
          ctx.translate(-MARK_CX, -MARK_CY);
          ctx.fillStyle = col(C2, 1);
          ctx.fill(MARK, MARK_FILL_RULE);
          ctx.restore();
        }
      }
      ctx.restore();
    }

    if (onMove) {
      // The reported radius covers the balls while the ring is open, and
      // the dedup key holds the ring's state, so opening and closing each
      // report fresh — the Rust side must move the click-through boundary
      // with it (PRODUCT-85).
      // `rad` stays the sphere's own: the boxes and the panel sit by it,
      // and must not jump when the ring opens.
      const hit = hitRadius(rad, ringOpen, rad + RING_GAP, BALL_R);
      const k = `${Math.round(orb.x)},${Math.round(orb.y)},${Math.round(rad)},${Math.round(hit)}`;
      if (k !== movedKey) {
        movedKey = k;
        onMove(orb.x, orb.y, rad, hit);
      }
    }

    // --- the one line (fades with its weight; the text comes from the server) ---
    if (lineW >= 0.02 && lineText) {
      const w = lineW,
        line = lineText;
      ctx.font =
        '500 14px Inter, "Helvetica Neue", Helvetica, Arial, sans-serif';
      const leftSide = orb.x > W / 2;
      const maxW = halfW - rad - 16 - 8; // what fits in the canvas beside the orb
      let shown = line;
      while (shown.length > 1 && ctx.measureText(shown).width + 24 > maxW)
        shown = shown.slice(1); // the tail stays visible
      if (shown !== line) shown = "…" + shown.slice(1);
      const tw = ctx.measureText(shown).width + 24,
        th = 30;
      const bx = leftSide ? orb.x - rad - 16 - tw : orb.x + rad + 16,
        by = orb.y - th / 2 + (1 - w) * 6;
      ctx.globalAlpha = w;
      ctx.fillStyle = "rgba(18,22,25,0.9)";
      ctx.strokeStyle = col(C2, 0.45);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(bx, by, tw, th, 8);
      mark(bx, by, bx + tw, by + th);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = "#e8ecec";
      ctx.textBaseline = "middle";
      ctx.fillText(shown, bx + 12, by + th / 2 + 0.5);
      ctx.globalAlpha = 1;
    }
    raf = window.setTimeout(() => frame(performance.now()), FRAME_MS);
  }
  raf = window.setTimeout(() => frame(performance.now()), FRAME_MS);

  return {
    /// State from the server, or from a local event (hotkey down → listening).
    setState,
    /// The mode the orb holds (blueprint D9): what the pill under the orb
    /// says until it changes. Unknown values are ignored.
    setMode(next: OrbMode) {
      if (!(next in MODE_WORD) || next === mode) return;
      prevMode = mode;
      mode = next;
      pillW = 0;
    },
    /// Whether a task lane runs (PRODUCT-68): the `task` pill beside the
    /// mode's word. Nothing calls it with true until the lane emits
    /// (PRODUCT-65).
    setTask(on: boolean) {
      task = on;
    },
    /// Microphone frames from the Rust side: pcm_s16le mono at the
    /// microphone's own rate, as captured for the server. The analysis is
    /// made for that rate, as the one for the reply's playback is for its.
    pushMic(pcm: Int16Array, rate: number = MIC_RATE) {
      if (rate !== micRate) {
        mic = createAnalysis(rate, P);
        micRate = rate;
      }
      mic.push(pcm);
      micAt = performance.now();
    },
    /// The playback analyser the speaking waves read.
    setOutput(node: AnalyserNode | null) {
      output = node;
      out = node ? createAnalysis(node.context.sampleRate, P) : null;
      obuf = node ? new Float32Array(new ArrayBuffer(node.fftSize * 4)) : null;
    },
    setLine(text: string) {
      lineText = text;
    },
    /// A tap on the orb (a press that did not move), with the state it landed in.
    onTap(fn: (state: OrbState) => void) {
      onTap = fn;
    },
    /// A right click on the orb's body (PRODUCT-86): the entry point to
    /// the hub window. Mirrors onTap; the app decides what it opens.
    onContextMenu(fn: () => void) {
      onContextMenu = fn;
    },
    /// The orb's position and radius whenever they change (CSS pixels),
    /// and the radius the window takes the pointer in: the sphere's, or,
    /// while the hover ring is open, one that covers the balls.
    onMove(fn: (x: number, y: number, r: number, hit: number) => void) {
      onMove = fn;
    },
    /// A click (a press that did not move) on one of the hover ring's
    /// mode balls (PRODUCT-85), with the mode it stands for.
    onBall(fn: (mode: OrbMode) => void) {
      onBall = fn;
    },
    /// A click on the dev feedback ball (PRODUCT-87's seam): the ball is
    /// drawn only while setDev(true) was called.
    onFeedback(fn: () => void) {
      onFeedback = fn;
    },
    /// Whether the feedback ball exists (PRODUCT-87's seam): the dev flag,
    /// from the `orb:dev` event. Default false.
    setDev(on: boolean) {
      dev = on;
    },
    /// The orb's diameter in CSS pixels, from the `orb:size` event (the
    /// Settings tab). Nothing is re-laid out: the radius is spring-driven
    /// towards P.size / 2 every frame, so the orb grows or shrinks into
    /// the new size the way it breathes. The Rust side bounds it
    /// (settings.rs) so it stays inside the canvas the orb is drawn in.
    setSize(size: number) {
      if (Number.isFinite(size) && size > 0) P.size = size;
    },
    /// Whether the cursor is over the window's interactive area, from the
    /// `orb:cursor` failsafe (PRODUCT-85): once the window turns
    /// click-through the webview sees no pointer events, so this is how
    /// the ring learns the cursor left. false closes it.
    setCursorInside(inside: boolean) {
      hoverEv = { kind: "cursor", inside };
    },
    /// Holds the hover ring shut while the gate's confirmation box is up
    /// (PRODUCT-85). The draft box and the panel do not block it.
    setRingBlocked(blocked: boolean) {
      ringBlocked = blocked;
    },
    /// Compact mode, from the `orb:compact` event (the Settings tab): the
    /// mark alone, small, docked at the bottom centre. The position is a
    /// target, not a jump — the same spring that carries a drag carries
    /// it to the dock and back. Leaving compact lets go of the target and
    /// the orb stays where the dock left it, to be dragged anywhere again.
    setCompact(on: boolean) {
      if (on === compact) return;
      compact = on;
      const change = compactChange(on, W, H);
      // A press still holding the orb to the cursor outlives the change,
      // and its next move would drag the mark off the dock it was just
      // given (Greptile on #86).
      if (change.dropDrag) {
        drag = null;
        lastPtr = null;
      }
      orb.target = change.target;
    },
    destroy() {
      clearTimeout(raf);
      removeEventListener("resize", resize);
    },
  };
}
